import React, { FC, useCallback } from 'react';

import type { RowClickedEvent } from 'ag-grid-community';
import { useFormikContext } from 'formik';

// Components
import BasicButton from '../../../../../../components/buttons/basicButton';
import BasicModal from '../../../../../../components/modals/basicModal';

// Constants
import aampeTheme from '../../../../../../constants/aampeTheme';

// Hooks
import useDatasetsQuery from '../../../../../../hooks/datasets/useDatasetByIdQuery';

// Models
import {
    DatasetFiltersModel,
    DatasetFiltersType,
} from '../../../../../../models/dataset-filters.model';
import { FormulaTestMessageFormValues } from 'models/formula.model';

import DatasetContentTable from '../../datasets/datasetConfigurationalModal/DatasetContentTable';

interface TestFormulaDatasetSelectionModalProps {
    datasetId: number;
    setIsExpanded: (value: boolean) => void;
    selectedDataset?: DatasetFiltersType;
}

const TestFormulaDatasetSelectionModal: FC<TestFormulaDatasetSelectionModalProps> =
    ({ datasetId, setIsExpanded, selectedDataset }) => {
        const { setFieldValue } =
            useFormikContext<FormulaTestMessageFormValues>();
        const { data: filteredDataset, isLoading: datasetLoading } =
            useDatasetsQuery(datasetId);

        const onDatasetRowClicked = useCallback(
            (event: RowClickedEvent<any>) => {
                let dataset_content: DatasetFiltersModel = {
                    id: 0,
                    dataset_id: datasetId,
                    filters: {},
                };

                Object.entries(event.data).forEach(([key, value]) => {
                    // don't include label key
                    // this is a custom key added only for label column
                    // this is not a valid dataset content key
                    if (key.includes('label_')) return;
                    if (key === 'id') {
                        dataset_content.id = Number(value);
                    } else {
                        dataset_content.filters[key] = [String(value)];
                    }
                });
                if (dataset_content.id) {
                    setFieldValue(
                        'filters.dataset_content',
                        dataset_content.filters,
                    );
                }
            },
            [datasetId, setFieldValue],
        );

        const onDatasetApply = useCallback(() => {
            setIsExpanded(false);
        }, [setIsExpanded]);

        return (
            <BasicModal
                width="50%"
                handleClose={() => setIsExpanded(false)}
                header={'Send test message > Dataset'}
                headerStyle={{
                    backgroundColor: aampeTheme.colors.backgroundWG02,
                    color: aampeTheme.colors.textAccessibleWG01,
                    fontSize: aampeTheme.size.font.text,
                    lineHeight: aampeTheme.size.lineHeight.default,
                    fontWeight: aampeTheme.weights.light,
                    padding: aampeTheme.spacing4(4, 6),
                }}
                footer={
                    <>
                        <BasicButton
                            id="Cancel"
                            isPrimary={false}
                            name={'Cancel'}
                            onClick={() => setIsExpanded(false)}
                        />
                        <BasicButton
                            id="Apply"
                            name={'Apply'}
                            onClick={onDatasetApply}
                            isDisabled={!selectedDataset}
                        />
                    </>
                }
            >
                <DatasetContentTable
                    isEditable={true}
                    datasetId={datasetId}
                    setRowCount={() => {}}
                    setDatasetFilters={() => {}}
                    filteredDataset={filteredDataset}
                    datasetLoading={datasetLoading}
                    onRowClicked={onDatasetRowClicked}
                    selectedDataset={selectedDataset}
                />
            </BasicModal>
        );
    };

export default TestFormulaDatasetSelectionModal;
